import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { theme, Card } from 'antd';
import ShopifyOrdersFilter from './components/ShopifyOrdersFilter';
import ShopifyOrdersList from './components/ShopifyOrdersList';
import KpiDashboard from './components/KpiDashboard';
import useUrlSync from './hooks/useUrlSync';
import './ShopifyOrders.css';
import { FILTER_TYPES, isValidFilterType } from './constants/filterTypes';

import {
  fetchOrderItems,
  fetchKpiMetrics,
  handleSearchChange,
  handleDateRangeChange,
  handleStoreChange,
  handleFinancialStatusChange,
  handleFulfillmentStatusChange,
  handleShipmentStatusChange,
  handleShipmentDetailChange,
  handleConfirmShipmentChange,
  handleCategoryChange,
  handleCollectionChange,
  handlePrinterChange,
  handleSupporterChange,
  handleTitleChange,
  handleSkuChange,
  handleEmployeeIdChange,
  handleExcludeTitleChange,
  handleExcludeSkuChange,
  handleBasecostChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
} from './actions';
import { setFilters, setPagination } from './reducers';

const ShopifyOrders = ({
  orderItems,
  loading,
  pagination,
  filters,
  kpiMetrics,
  user,
  // Action creators
  fetchOrderItems,
  fetchKpiMetrics,
  handleSearchChange,
  handleDateRangeChange,
  handleStoreChange,
  handleFinancialStatusChange,
  handleFulfillmentStatusChange,
  handleShipmentStatusChange,
  handleShipmentDetailChange,
  handleConfirmShipmentChange,
  handleCategoryChange,
  handleCollectionChange,
  handlePrinterChange,
  handleSupporterChange,
  handleTitleChange,
  handleSkuChange,
  handleEmployeeIdChange,
  handleExcludeTitleChange,
  handleExcludeSkuChange,
  handleBasecostChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  setFilters,
  setPagination
}) => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const {
    searchText,
    selectedDateRange,
    selectedStore,
    selectedFinancialStatus,
    selectedFulfillmentStatus,
    selectedCategory,
    selectedCollection,
    selectedPrinter,
    selectedSupporter
  } = filters;

  const [isInitialized, setIsInitialized] = useState(false);

  // Lock employee filter for non-admin users with employeeCode
  const isAdminRole = user?.userRole?.name === 'Super Admin' || user?.userRole?.name === 'Admin';
  const lockedEmployeeId = (!isAdminRole && user?.employeeCode) ? user.employeeCode : null;

  // URL synchronization handlers
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, [setFilters]);

  const handlePaginationChangeCallback = useCallback((newPagination) => {
    setPagination(newPagination);
  }, [setPagination]);

  const { updateUrl } = useUrlSync(
    filters,
    pagination,
    handleFiltersChange,
    handlePaginationChangeCallback
  );

  // Initial data fetch
  useEffect(() => {
    if (!isInitialized) {
      // Pre-set employee filter trước khi fetch
      if (lockedEmployeeId) {
        handleEmployeeIdChange(lockedEmployeeId);
      }
      fetchOrderItems(pagination.current, pagination.pageSize, updateUrl);
      fetchKpiMetrics();
      setIsInitialized(true);
    }
  }, [isInitialized, fetchOrderItems, fetchKpiMetrics, pagination.current, pagination.pageSize, updateUrl, lockedEmployeeId, handleEmployeeIdChange]);

  // Handlers for filters
  const filterHandlers = useMemo(() => ({
    [FILTER_TYPES.SEARCH]: handleSearchChange,
    [FILTER_TYPES.DATE_RANGE]: handleDateRangeChange,
    [FILTER_TYPES.STORE]: handleStoreChange,
    [FILTER_TYPES.FINANCIAL_STATUS]: handleFinancialStatusChange,
    [FILTER_TYPES.FULFILLMENT_STATUS]: handleFulfillmentStatusChange,
    [FILTER_TYPES.SHIPMENT_STATUS]: handleShipmentStatusChange,
    [FILTER_TYPES.SHIPMENT_DETAIL]: handleShipmentDetailChange,
    [FILTER_TYPES.CONFIRM_SHIPMENT]: handleConfirmShipmentChange,
    [FILTER_TYPES.CATEGORY]: handleCategoryChange,
    [FILTER_TYPES.COLLECTION]: handleCollectionChange,
    [FILTER_TYPES.PRINTER]: handlePrinterChange,
    [FILTER_TYPES.SUPPORTER]: handleSupporterChange,
    [FILTER_TYPES.TITLE]: handleTitleChange,
    [FILTER_TYPES.SKU]: handleSkuChange,
    [FILTER_TYPES.EMPLOYEE_ID]: handleEmployeeIdChange,
    [FILTER_TYPES.EXCLUDE_TITLE]: handleExcludeTitleChange,
    [FILTER_TYPES.EXCLUDE_SKU]: handleExcludeSkuChange,
    [FILTER_TYPES.BASECOST]: handleBasecostChange
  }), [
    handleSearchChange,
    handleDateRangeChange,
    handleStoreChange,
    handleFinancialStatusChange,
    handleFulfillmentStatusChange,
    handleShipmentStatusChange,
    handleShipmentDetailChange,
    handleConfirmShipmentChange,
    handleCategoryChange,
    handleCollectionChange,
    handlePrinterChange,
    handleSupporterChange,
    handleTitleChange,
    handleSkuChange,
    handleEmployeeIdChange,
    handleExcludeTitleChange,
    handleExcludeSkuChange,
    handleBasecostChange
  ]);

  const handleFilterChange = useCallback((filterType, value) => {
    if (!isValidFilterType(filterType)) {
      console.warn(`Invalid filter type: ${filterType}. Available types:`, Object.values(FILTER_TYPES));
      return;
    }
    const handler = filterHandlers[filterType];
    if (handler) {
      handler(value);
    } else {
      console.warn(`No handler found for filter type: ${filterType}`);
    }
  }, [filterHandlers]);

  const onApplyFilters = useCallback(() => {
    applyFilters(updateUrl);
  }, [applyFilters, updateUrl]);

  const onClearFilters = useCallback(() => {
    handleClearFilters(updateUrl);
    // Khôi phục locked employee filter sau khi clear
    if (lockedEmployeeId) {
      setTimeout(() => {
        handleEmployeeIdChange(lockedEmployeeId);
        applyFilters(updateUrl);
      }, 0);
    }
  }, [handleClearFilters, updateUrl, lockedEmployeeId, handleEmployeeIdChange, applyFilters]);

  const onReload = useCallback(() => {
    fetchOrderItems(pagination.current, pagination.pageSize, updateUrl);
    fetchKpiMetrics();
  }, [fetchOrderItems, fetchKpiMetrics, pagination.current, pagination.pageSize, updateUrl]);

  const onPaginationChange = useCallback((page, pageSize) => {
    handlePaginationChange(page, pageSize, updateUrl);
  }, [handlePaginationChange, updateUrl]);

  return (
    <div className="shopify-orders-container">
      <div
        style={{
          marginBottom: 16
        }}
      >
        <KpiDashboard metrics={kpiMetrics} loading={loading && !isInitialized} onFilterChange={handleFilterChange} onApplyFilters={onApplyFilters} />
      </div>
      <ShopifyOrdersFilter
        onFilterChange={handleFilterChange}
        onClearFilters={onClearFilters}
        onApplyFilters={onApplyFilters}
        onReload={onReload}
        filters={filters}
        loading={loading}
        lockedEmployeeId={lockedEmployeeId}
      />
      <div
        style={{
          padding: '5px 16px 16px 16px',
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
          minHeight: 280,
        }}
      >

        <ShopifyOrdersList
          orderItems={orderItems}
          isLoading={loading}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          user={user}
          filters={filters}
        />
      </div>
    </div>
  );
};

// Map state to props
const mapStateToProps = (state) => ({
  orderItems: state.shopifyOrders.orderItems,
  loading: state.shopifyOrders.loading,
  pagination: state.shopifyOrders.pagination,
  filters: state.shopifyOrders.filters,
  kpiMetrics: state.shopifyOrders.kpiMetrics,
  user: state.auth.user,
});

// Map dispatch to props
const mapDispatchToProps = {
  fetchOrderItems,
  fetchKpiMetrics,
  handleSearchChange,
  handleDateRangeChange,
  handleStoreChange,
  handleFinancialStatusChange,
  handleFulfillmentStatusChange,
  handleShipmentStatusChange,
  handleShipmentDetailChange,
  handleConfirmShipmentChange,
  handleCategoryChange,
  handleCollectionChange,
  handlePrinterChange,
  handleSupporterChange,
  handleTitleChange,
  handleSkuChange,
  handleEmployeeIdChange,
  handleExcludeTitleChange,
  handleExcludeSkuChange,
  handleBasecostChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  setFilters,
  setPagination
};

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyOrders);
